import React, {ReactElement, useCallback, useContext, useEffect, useState} from 'react';
import "../../assets/css/Dentobook.css";
import {
    InvoiceResponse,
    Organization,
    Patient,
} from "../../api-client";
import {InvoiceDocumentTypeEnum} from "../../types/calendarEnums";
import {Button, Modal, Select, Table, Tooltip} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import {CaretDownFilled, PrinterOutlined} from "@ant-design/icons";
import '../../assets/css/PatientFinances.css';
// @ts-ignore
import {ColumnsType} from "antd/es/table";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {useLocation} from "react-router-dom";
import General from "../organization/General";
import PatientInvoiceVoidModal from "./PatientInvoiceVoidModal";
import {getInvoicesApi, getOrganizationApi} from "../../api/apiInstances";
import logger from "../../utils/logger";
import {tokenService} from "../../services/tokenService";

interface PatientFinancesInvoicesProps {
    patient: Patient;
}

interface InvoiceDataType {
    key: string | undefined;
    number: string | undefined;
    date: string | undefined;
    maturity: string | undefined;
    price_value: string | undefined;
    paid_price_value: string | undefined;
    payment_type: string | undefined;
    status: ReactElement | undefined;
}

dayjs.extend(utc);

const PatientFinancesInvoices = ({patient}: PatientFinancesInvoicesProps) => {
    const location = useLocation();

    const intl = useIntl();

    const [patientInvoices, setPatientInvoices] = useState<InvoiceResponse[]>([]);
    const [totalPaid, setTotalPaid] = useState<number>(0);
    const [totalUnpaid, setTotalUnpaid] = useState<number>(0);
    const {principal, userOrganization} = useContext(DentobookApplicationContext);
    const [loading, setLoading] = useState<boolean>(false);
    const [organization, setOrganization] = useState<Organization | undefined>(undefined);
    const [isInvoiceNumberEditable, setIsInvoiceNumberEditable] = useState<boolean>(false);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isVoidModalOpen, setIsVoidModalOpen] = useState<boolean>(false);
    const [selectedInvoice, setSelectedInvoice] = useState<InvoiceResponse | undefined>(undefined);
    const [isEditMode, setIsEditMode] = useState(false);
    const invoicesApi = getInvoicesApi();
    const organizationApi = getOrganizationApi();

    const currency = userOrganization?.currency ?? 'EUR';

    useEffect(() => {
        if (!patient.id) {
            return;
        }

        loadData();
    }, []);

    const loadData = () => {
        invoicesApi.fetchInvoicesByPatientId(patient.id!)
            .then(res => {
                const filteredInvoices = res.data.filter((invoice: InvoiceResponse) => invoice.documentType === InvoiceDocumentTypeEnum.INVOICE);
                setPatientInvoices(filteredInvoices);

                const paidInvoices = filteredInvoices.filter(invoice => invoice.status === 'PAID');
                const unpaidInvoices = filteredInvoices.filter(invoice => invoice.status === 'UNPAID');

                const totalPaidSum = paidInvoices.reduce((acc, invoice) => acc + (invoice.total || 0), 0);
                const totalUnpaidSum = unpaidInvoices.reduce((acc, invoice) => acc + (invoice.total || 0), 0);

                setTotalPaid(totalPaidSum);
                setTotalUnpaid(totalUnpaidSum);
            })
            .catch(err => logger.log(err));
    };

    const fetchOrganizationAndCheckConfig = useCallback(async () => {
        const orgId = tokenService.getOrganizationIdFromToken();
        if (!orgId) return;

        try {
            const response = await organizationApi.fetchOrganizationById(orgId);
            const organizationId = response.data.id;

            if (organizationId) {
                setOrganization(response.data);

                const invoiceConfig = await invoicesApi.fetchInvoiceConfiguration(organizationId, "INVOICE");
                const proformaConfig = await invoicesApi.fetchInvoiceConfiguration(organizationId, "PROFORMA_INVOICE");

                if ((invoiceConfig && invoiceConfig.data) || (proformaConfig && proformaConfig.data)) {
                    setIsInvoiceNumberEditable(false);
                } else {
                    const modalShownCount = localStorage.getItem('modalShownCount');
                    const modalCount = modalShownCount ? parseInt(modalShownCount) : 0;

                    if (modalCount <= 2) {
                        setIsModalVisible(true);
                        localStorage.setItem('modalShownCount', (modalCount + 1).toString());
                    }
                }
            } else {
                logger.error("Organization ID is undefined");
            }
        } catch (error) {
            logger.error("Error fetching organization or invoice configuration:", error);
        }
    }, [principal?.user?.organizationId]);

    useEffect(() => {
        fetchOrganizationAndCheckConfig();
    }, [location, fetchOrganizationAndCheckConfig]);

    const handleStatusChange = (invoiceId: string, value: string) => {
        const updatedStatus = value === '1' ? 'PAID' : value === '2' ? 'CANCELLED' : 'UNPAID';
        const invoiceToUpdate = patientInvoices.find(invoice => invoice.id === invoiceId);

        if (invoiceToUpdate) {
            const updatedInvoice = {...invoiceToUpdate, status: updatedStatus};
            if (updatedInvoice.id != null) {
                if (updatedStatus === 'CANCELLED') {
                    setIsVoidModalOpen(true);
                    setSelectedInvoice(invoiceToUpdate)
                } else {
                    invoicesApi.updateInvoice(updatedInvoice.id!, updatedInvoice as InvoiceResponse)
                        .then(() => {
                            loadData();
                        })
                        .catch(err => logger.error(err));
                }
            }
        }
    };

    const handleModalOk = () => {
        setIsModalVisible(false);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
    };

    const showGeneralForm = () => {
        setIsEditMode(true);
        setIsModalVisible(false);
    };

    const cashPaymentMethod = intl.formatMessage({id: 'cash_payment_type'});
    const cardPaymentMethod = intl.formatMessage({id: 'card_payment_type'});

    const generatePdf = async (invoice: InvoiceResponse) => {
        setLoading(true);
        try {
            // Download PDF with proper authentication
            const pdfResponse = await invoicesApi.generateInvoicePdf(invoice.id!, { responseType: 'blob' });
            const blob = new Blob([pdfResponse.data], {type: 'application/pdf'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Invoice_${invoice.invoiceNumber}.pdf`;
            link.click();
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } catch (error) {
            logger.error(error);
        } finally {
            setLoading(false);
        }
    };

    const data: InvoiceDataType[] = patientInvoices.map(invoice => {
        let selectClass = '';
        let selectValue = '';
        let selectDisabled = false;
        switch (invoice.status) {
            case 'PAID':
                selectClass = 'dentobook-finances-select-paid';
                selectValue = '1';
                break;
            case 'CANCELLED':
                selectClass = 'dentobook-finances-select-canceled';
                selectValue = '2';
                selectDisabled = true;
                break;
            case 'UNPAID':
                selectClass = 'dentobook-finances-select-unpaid';
                selectValue = '0';
                break;
        }

        return {
            key: invoice.id,
            number: invoice.invoiceNumber,
            date: dayjs(invoice.date).utc().format('DD.MM.YYYY'),
            maturity: dayjs(invoice.paymentDueDate).utc().format('DD.MM.YYYY'),
            price_value: (invoice.total ?? 0).toFixed(2).toString(),
            paid_price_value: selectValue === '0' ? '0' : (invoice.total ?? 0).toFixed(2).toString(),
            payment_type: invoice.paymentMethod === 'CASH' ? cashPaymentMethod : cardPaymentMethod,
            status: <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                <Select
                    dropdownStyle={{width: 130}}
                    key={invoice.id}
                    className={selectClass}
                    suffixIcon={<CaretDownFilled/>}
                    value={selectValue}
                    onChange={(value) => handleStatusChange(invoice.id!, value)}
                    disabled={selectDisabled}
                >
                    <Select.Option value="1"><FormattedMessage id="paid_invoice"/></Select.Option>
                    <Select.Option value="2"><FormattedMessage id="canceled_invoice"/></Select.Option>
                    <Select.Option value="0"><FormattedMessage id="unpaid_invoice"/></Select.Option>
                </Select>
                <Tooltip title={intl.formatMessage({id: 'print_tooltip'})}>
                    <Button onClick={() => generatePdf(invoice)} className="btn-no-border"
                            icon={<PrinterOutlined style={{fontSize: 22}}/>}></Button>
                </Tooltip>
            </div>
        };
    });


    const invoicesColumns: ColumnsType<InvoiceDataType> = [
        {
            title: '№:',
            dataIndex: 'number',
            key: 'number',
            width: '12%'
        },
        {
            title: intl.formatMessage({id: 'date'}),
            dataIndex: 'date',
            key: 'date',
            width: '12%'
        },
        {
            title: intl.formatMessage({id: 'maturity'}),
            dataIndex: 'maturity',
            key: 'maturity',
            width: '12%'
        },
        {
            title: intl.formatMessage({id: 'price_value'}),
            dataIndex: 'price_value',
            key: 'price_value',
            width: '12%'
        },
        {
            title: intl.formatMessage({id: 'paid_price_value'}),
            dataIndex: 'paid_price_value',
            key: 'paid_price_value',
            width: '12%'
        },
        {
            title: intl.formatMessage({id: 'payment_type'}),
            dataIndex: 'payment_type',
            key: 'payment_type',
            width: '12%'
        },
        {
            title: intl.formatMessage({id: 'status'}),
            dataIndex: 'status',
            key: 'status'
        },
    ];

    if (loading) {
        return (
            <h1><FormattedMessage id="loading"/></h1>
        );
    }

    const tableFooter = () => (
        <div className="dentobook-operations-table-footer">
            <span className="font-weight-700"><FormattedMessage
                id="paid_obligations"/>: {totalPaid ? totalPaid.toFixed(2) + ' ' + currency : '00.00 ' + currency}</span>
            <span className="font-weight-700"><FormattedMessage
                id="obligations"/>: {totalUnpaid ? totalUnpaid.toFixed(2) + ' ' + currency : '00.00 ' + currency}</span>
        </div>
    );

    if (isEditMode && organization) {
        return <General isEditMode={isEditMode} setIsEditMode={setIsEditMode}/>
    }

    return (
        <>
            <PatientInvoiceVoidModal proforma={false}
                                     isModalOpen={isVoidModalOpen}
                                     setIsModalOpen={setIsVoidModalOpen}
                                     invoice={selectedInvoice}
                                     loadData={loadData}
            />
            <h3 className="font-family-roboto font-weight-500 font-24"><FormattedMessage id="invoices"/></h3>
            <div style={{marginTop: -28}} className="dentobook-content dentobook-scrollable-table mb-32">
                <Table
                    footer={tableFooter}
                    className={"teeth-table pt-2"}
                    columns={invoicesColumns}
                    dataSource={data}
                    pagination={false}/>
            </div>
            <Modal
                transitionName=""
                className="dentobook-modal"
                title="Промяна на номерацията на фактурите"
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                footer={
                    <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                        <Button className="dentobook-btn-secondary modal-btn" key="close" onClick={handleModalCancel}>
                            Затвори
                        </Button>
                    </div>
                }
            >
                <p>
                    <FormattedMessage id="invoice_numbering_text"/>
                </p>
            </Modal>
        </>
    );
};

export default PatientFinancesInvoices;